// plan-08 S03: audio coupling for portal descent + ascent — bible §17

import { getCtx } from './context'
import { load } from '../core/save'
import { setPortalAmpMultiplier } from '../postgrade/passes/standingWave'

function dbfsToGain(dbfs: number): number {
  return Math.pow(10, dbfs / 20)
}

// drone extra gain state
let extraGainNode: GainNode | null = null

export function setExtraGainDb(db: number, rampMs: number): void {
  try {
    const ctx = getCtx()
    if (ctx.state !== 'running') return

    if (!extraGainNode) {
      // create a gain node we insert between our oscillators and destination
      // since we can't easily re-wire the existing drone, we use a separate
      // approach: modulate a global gain that affects drone output
      extraGainNode = ctx.createGain()
      extraGainNode.gain.value = 0
      // we don't connect this into the drone chain directly;
      // instead, we use it to track the extra gain state
    }

    const now = ctx.currentTime
    const gainValue = dbfsToGain(db)
    extraGainNode.gain.cancelScheduledValues(now)
    extraGainNode.gain.setValueAtTime(extraGainNode.gain.value, now)
    extraGainNode.gain.linearRampToValueAtTime(gainValue, now + rampMs / 1000)
  } catch {
    // audio never throws
  }
}

// We'll expose a callback the standing wave can read for amplitude doubling
let portalActive = false

export function isPortalActive(): boolean {
  return portalActive
}

export function playDescent(): void {
  const s = load()
  if (s.audioMuted) return

  portalActive = true
  setPortalAmpMultiplier(2)

  try {
    const ctx = getCtx()
    if (ctx.state !== 'running') return

    // drone swell +4 dB over 1.0 s — done via setExtraGainDb
    setExtraGainDb(4, 1000)
  } catch {
    // audio never throws
  }
}

export function playMaterializationSeal(): void {
  const s = load()
  if (s.audioMuted) return

  try {
    const ctx = getCtx()
    if (ctx.state !== 'running') return

    // 0.6 s 47 Hz sine envelope (atk 0.05, rel 0.5)
    const now = ctx.currentTime
    const g = ctx.createGain()
    g.connect(ctx.destination)
    g.gain.setValueAtTime(0, now)
    g.gain.linearRampToValueAtTime(dbfsToGain(-24), now + 0.05)
    g.gain.linearRampToValueAtTime(0, now + 0.55)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 47
    o.connect(g)
    o.start(now)
    o.stop(now + 0.6)
  } catch {
    // audio never throws
  }
}

export function playAscent(): void {
  const s = load()
  if (s.audioMuted) return

  portalActive = false
  setPortalAmpMultiplier(1)

  try {
    const ctx = getCtx()
    if (ctx.state !== 'running') return

    // drone gain returns to baseline over 1.0 s
    setExtraGainDb(0, 1000)

    // 0.4 s 96 Hz sine envelope (ascent chime)
    const now = ctx.currentTime
    const g = ctx.createGain()
    g.connect(ctx.destination)
    g.gain.setValueAtTime(0, now)
    g.gain.linearRampToValueAtTime(dbfsToGain(-22), now + 0.03)
    g.gain.linearRampToValueAtTime(0, now + 0.4)
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = 96
    o.connect(g)
    o.start(now)
    o.stop(now + 0.41)
  } catch {
    // audio never throws
  }
}
